import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { MentionsInput as ReactMentionsInput, Mention } from 'react-mentions';

import User from '../../User';

import styles from './MentionsInput.module.scss';

const MentionsInput = React.memo(
  ({ value, users, placeholder, onValueChange, onKeyDown, onFocus, onBlur, inputRef }) => {
    const handleChange = useCallback(
      (event, newValue) => {
        onValueChange(newValue);
      },
      [onValueChange],
    );

    const renderSuggestion = useCallback(
      (suggestion, search, highlightedDisplay, index, focused) => (
        <div className={`${styles.suggestion} ${focused ? styles.suggestionFocused : ''}`}>
          <User name={suggestion.display} avatarUrl={suggestion.avatarUrl} size="tiny" />
          <span className={styles.suggestionName}>{suggestion.display}</span>
        </div>
      ),
      [],
    );

    const displayTransform = useCallback((id, display) => `@${display}`, []);

    // Transform board memberships to react-mentions format
    // Each membership has a nested 'user' object with name and avatarUrl
    const mentionData = users.map((membership) => ({
      id: membership.user?.id || membership.id,
      display: membership.user?.name || membership.name || 'Unknown',
      avatarUrl: membership.user?.avatarUrl || membership.avatarUrl,
    }));

    const mentionsStyle = {
      control: {
        minHeight: 40,
      },
      input: {
        overflow: 'auto',
      },
      suggestions: {
        list: {
          maxHeight: 200,
          overflow: 'auto',
        },
      },
    };

    return (
      <ReactMentionsInput
        inputRef={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        className="mentions"
        classNames={styles}
        style={mentionsStyle}
        allowSpaceInQuery
        a11ySuggestionsListLabel="Suggested users"
      >
        <Mention
          trigger="@"
          data={mentionData}
          markup="@[__display__](user:__id__)"
          renderSuggestion={renderSuggestion}
          displayTransform={displayTransform}
          className={styles.mentionHighlight}
          appendSpaceOnAdd
        />
      </ReactMentionsInput>
    );
  },
);

MentionsInput.propTypes = {
  value: PropTypes.string.isRequired,
  users: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  placeholder: PropTypes.string,
  onValueChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  inputRef: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

MentionsInput.defaultProps = {
  placeholder: '',
  onKeyDown: undefined,
  onFocus: undefined,
  onBlur: undefined,
  inputRef: undefined,
};

export default MentionsInput;
